import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import {useTranslation} from 'react-i18next';
import {FormHelperText} from '@mui/material';
import {toast} from "react-toastify";
import Cookies from "js-cookie";
import {CustomList, CustomStyleFormHelperText, CustomStyleTextField} from "../Styles";
import CustomAutoCompleteMenuOption from "../CustomAutoCompleteMenuOption";
import {CustomPaperAutocomplete, CustomPopperAutocomplete} from "../CustomComponentsPropsAutoComplete";


/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * ⚠️Component used **only when this component is rendered inside <CustomAddChip />**
 *
 * @param String id: Autocomplete identifier (required).
 * @param {Array<Object>} data - List of objects containing id and label (required).
 * @param {Object | String | Number} value - Selected value (required).
 * @param {Function} setValue - State setter to update the selected value (required).
 * @param {Function} handleFn - Callback fired when a value is selected (required).
 * @param {String} label - Label displayed above the input (required).
 * @param {String} defaultState - Default input value (optional).
 * @param {Boolean} required - Indicates if the field is required (required).
 * @param {Boolean} disabled - Disables the input when true (required).
 * @param {Boolean} emptyFields - Indicates if the field must show an empty error state (required).
 * @param {String} helperText - Helper/legend text shown below the field (optional).
 * @param {Boolean | "touch" | "mouse"} blurOnSelect - Controls whether the input loses focus on selection.
 * @param {Boolean} reset - When true, resets the value after selecting an option (optional).
 * @param {Function} onKeyDownEnter - Callback executed when Enter is pressed and a value is selected (optional).
 * @param {JSX.Element | Boolean} noOptionsCard - Custom element shown when no options are available (optional).
 * @param {Boolean} showRenderOption - Enables the custom renderOption component (optional).
 * @param {Function} filterOptions - Custom filtering logic for the Autocomplete list (optional).
 * @param {String} inputValue - Controlled text input value for search (optional).
 * @param {Function} onInputChange - Callback fired when the text input changes (optional).
 * @param {JSX.Element|null} searchIcon - Custom icon displayed as search/popup indicator.
 * @param {JSX.Element|null} clearIcon - Custom icon used to clear the input field.
 * @param {number} [length=100] - Maximum number of characters allowed in the input.
 * @param {boolean} [open] - Controls whether the autocomplete dropdown is open externally.
 * @param {function} [setOpen] - Setter to control dropdown open/close state externally.
 * @param {Array} [selectedList] - Current list of selected items (used for validation and UI state).
 * @param {object} [itemSelected] - Currently selected item before being added as a chip.
 * @param {string} [placeholder] - Placeholder text displayed inside the input field.
 * @param {object} [positionIconOptions] - Configuration for icon positioning inside options list.
 * @param {boolean} [isValid=true] - Indicates if current input passes validation rules.
 * @param {string} [isValidText] - Message or translation key shown when validation fails.
 * @param {boolean} [onlyOnKeyDown] - If true, disables auto search and triggers search only via keyboard (Enter).
 * @param {Boolean} iconFullHeight - Draws the left icon of every option in a column of its own, so it covers the label and its subsection (optional).
 * @returns {JSX.Element}
 */
export default function AddChipAutoComplete({
                                               id = 'slt_acs_autocomplete',
                                               data,
                                               value,
                                               setValue,
                                               handleFn,
                                               label,
                                               defaultState,
                                               required = true,
                                               disabled = false,
                                               emptyFields = false,
                                               helperText = '',
                                               blurOnSelect = false,
                                               reset = true,
                                               onKeyDownEnter = () => {},
                                               noOptionsCard = false,
                                               showRenderOption=false,
                                               filterOptions=undefined,
                                               inputValue,
                                               onInputChange,
                                               searchIcon,
                                               clearIcon=undefined,
                                               length= 100,
                                               open,
                                               setOpen,
                                               selectedList,
                                               itemSelected,
                                               placeholder,
                                               positionIconOptions,
                                               isValid=true,
                                               isValidText,
                                               onlyOnKeyDown,
                                                iconFullHeight = false
                                           }) {
    const {t} = useTranslation();
    const options = Array.isArray(data) ? data : [];
    const lCode = Cookies.get('i18next') || 'en';
    let optionalProps = {}
    const valueEmpty = value === null || value === '';

    if (showRenderOption){
        optionalProps = {
            renderOption: (props, item) => (
                <li style={CustomList} {...props}  key={item.id || item.label}>
                    <CustomAutoCompleteMenuOption
                        label={item.label}
                        subSection={item.subSection}
                        labelExtra={item.labelExtra}
                        Icon={item.Icon}
                        positionIconOptions={positionIconOptions}
                        iconFullHeight={iconFullHeight}
                    />
                </li>
            )}
    }

    if(typeof searchIcon !== "undefined" ){
        optionalProps = {
            ...optionalProps,
            componentsProps:{
                popupIndicator: {
                    disableRipple: true,
                }
            },
            popupIcon: <span title="" >{searchIcon}</span>
        }
    }

    if(typeof onInputChange !== "undefined"){
        optionalProps = {...optionalProps, onInputChange: onInputChange }
    }

    if(typeof inputValue !== "undefined"){
        optionalProps = {...optionalProps, inputValue: inputValue }
    }

    if(typeof clearIcon !== "undefined"){
        optionalProps = {...optionalProps, clearIcon: clearIcon }
    }

    if (typeof open !== "undefined" && typeof setOpen !== "undefined") {
        optionalProps = { ...optionalProps, open: open, onClose: () => { if(setOpen) setOpen(false) }};
    }

    const getHelperText = (text) => {
        return  <FormHelperText
            className={'css-k4qjio-MuiFormHelperText-root'}
            sx={{...CustomStyleFormHelperText(false)}}
        >
            { text }
        </FormHelperText>
    }

    const getErrorText = (text) => {
        return  <FormHelperText error={true} sx={CustomStyleFormHelperText}>
            {text}
        </FormHelperText>
    }

    return (
        <div className={`${(!emptyFields && (value || helperText)) ? "": !emptyFields && selectedList?.length === 0 ? "input-padding-bottom" : ""}`}>
            <Autocomplete
                sx={CustomStyleTextField(lCode, searchIcon)}
                required
                size='small'
                disabled={disabled}
                id={id}
                PopperComponent={CustomPopperAutocomplete} // Options container
                PaperComponent={CustomPaperAutocomplete}//Box Container
                options={options}
                value={value}
                blurOnSelect={blurOnSelect}
                className={(emptyFields && selectedList?.length === 0) ? "input-required" : ""}
                onChange={(event, newValue) => {
                    if (newValue) {
                        setValue(newValue);
                        handleFn(newValue);
                        if (reset) setValue(null);
                    } else {
                        setValue(null);
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        if (!value) toast.warning(t("select_an_option"));
                        else onKeyDownEnter(event);
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        id={id}
                        label={label ? label : t('search')}
                        required={required}
                        error={emptyFields && selectedList?.length === 0}
                        inputProps={{
                            ...params.inputProps,
                            maxLength: length
                        }}
                        placeholder={placeholder}
                    />
                )}
                noOptionsText={
                    <>
                        {noOptionsCard
                            ?
                                <div className={"p-0 m-0"}>
                                    {noOptionsCard}
                                </div>
                            :
                                t('no_options')
                        }
                    </>
                }
                filterOptions={filterOptions}
                error={(emptyFields && valueEmpty) || (!isValid)}
                {...optionalProps}
            />

            {emptyFields && selectedList?.length === 0 ? (
                   getErrorText(
                        value ?
                                !itemSelected?.id ? onlyOnKeyDown ? t('press_enter_to_search') : t('you_must_select_an_option') :t('click_to_add')
                           :
                                t('requiredField')
                   ))
                :
                <>
                    { isValid && value ?
                            getHelperText(!itemSelected?.id ? onlyOnKeyDown ? t('press_enter_to_search') : t('you_must_select_an_option') :t('click_to_add'))
                        :
                        !isValid && !emptyFields ? <FormHelperText error={true} sx={CustomStyleFormHelperText}>{t(isValidText)}</FormHelperText>
                        :
                        helperText && !emptyFields && getHelperText(helperText)
                    }
                </>
            }
        </div>
    );
}
